import visualPolishTuning from '../../../data/cpu-visual-polish.json';

export type PlayerPresentationMotionKind =
  | 'none'
  | 'quarterback-footwork'
  | 'quarterback-release'
  | 'trench-hand-fight'
  | 'coverage-posture'
  | 'ball-tracking'
  | 'catch-anticipation'
  | 'route-body-language'
  | 'route-break'
  | 'carrier-lean'
  | 'contact-impact'
  | 'celebration';

export type PlayerPresentationTransform = {
  kind: PlayerPresentationMotionKind;
  offsetX: number;
  offsetY: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
};

const TAU = Math.PI * 2;
const TRENCH_ROLES = new Set(['LT', 'LG', 'C', 'RG', 'RT', 'DE1', 'DT1', 'DT2', 'DE2']);
const COVERAGE_ROLES = new Set(['CB1', 'CB2', 'CB3', 'S1', 'S2', 'LB1', 'LB2', 'LB3']);
const ROUTE_ROLES = new Set(['WR1', 'WR2', 'WR3', 'SLOT', 'TE', 'RB', 'FB']);

const IDENTITY_TRANSFORM: PlayerPresentationTransform = {
  kind: 'none',
  offsetX: 0,
  offsetY: 0,
  rotation: 0,
  scaleX: 1,
  scaleY: 1,
};

function rolePhase(role: string): number {
  let hash = 0;
  for (let index = 0; index < role.length; index += 1) {
    hash = (hash * 33 + role.charCodeAt(index)) % 997;
  }
  return hash;
}

function rawAnimationTick(frame: any): number {
  if (Number.isFinite(frame?.simFrame)) {
    return Number(frame.simFrame);
  }
  if (Number.isFinite(frame?.liveProgress)) {
    return Math.round(Number(frame.liveProgress) * 360);
  }
  return 0;
}

function animationTick(frame: any): number {
  const rawTick = rawAnimationTick(frame);
  const stepFrames = Math.max(1, visualPolishTuning.proceduralCadence.stepFrames);
  return Math.floor(rawTick / stepFrames) * stepFrames;
}

function wave(tick: number, phase: number, period: number): number {
  return Math.sin(((tick + phase) / period) * TAU);
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function smoothstep(value: number): number {
  const clamped = clamp01(value);
  return clamped * clamped * (3 - 2 * clamped);
}

function celebrationTransform(player: any, frame: any, teamType: string, facing: number): PlayerPresentationTransform | null {
  const moment = frame?.celebrationMoment;
  if (!moment || moment.role !== player?.role || moment.teamType !== teamType) return null;
  const progress = clamp01(Number(moment.progress || 0));
  const arc = Math.sin(progress * Math.PI);
  const style = String(moment.style || 'none');
  if (style === 'signature') {
    return { kind: 'celebration', offsetX: facing * progress * 3, offsetY: -arc * 12, rotation: progress * TAU, scaleX: 1, scaleY: 1 };
  }
  // Legacy celebration values remain valid persisted data, but intentionally
  // render as no celebration after the Season 4 presentation simplification.
  return { ...IDENTITY_TRANSFORM };
}

export function resolveCelebrationBallOffset(frame: any): { offsetX: number; offsetY: number } {
  const moment = frame?.celebrationMoment;
  const style = String(moment?.style || 'none');
  const progress = clamp01(Number(moment?.progress || 0));
  const facing = Number(frame?.direction) < 0 ? -1 : 1;

  if (style === 'signature') {
    return { offsetX: facing * progress * 3, offsetY: -Math.sin(progress * Math.PI) * 12 };
  }
  return { offsetX: 0, offsetY: 0 };
}

function isPresentationPlayer(player: any): boolean {
  return !!player?.arcadeState
    || !!player?.arcadeAssignment
    || !!player?.engagementState;
}

function isGroundedOrFinishing(player: any): boolean {
  const assignment = String(player?.arcadeAssignment || '');
  return !!player?.down
    || !!player?.tackled
    || Number(player?.tackleProgress || 0) >= 0.7
    || assignment.startsWith('catch-dive-')
    || assignment === 'catch-secure-dive'
    || assignment === 'sack-finish';
}

function quarterbackFootwork(
  player: any,
  tick: number,
  phase: number,
  facing: number,
): PlayerPresentationTransform {
  const planted = !player?.moving
    || player?.moveStyle === 'set'
    || player?.moveStyle === 'idle';
  const cadence = wave(tick, phase, planted ? 26 : 16);
  const step = wave(tick, phase + 5, planted ? 13 : 10);
  const lift = Math.abs(step);

  return {
    kind: 'quarterback-footwork',
    offsetX: planted ? facing * cadence * 0.22 : facing * cadence * 0.34,
    offsetY: -lift * (planted ? 0.48 : 0.7),
    rotation: facing * (planted ? 0.012 + cadence * 0.012 : -0.018 + cadence * 0.014),
    scaleX: 1 + lift * 0.012,
    scaleY: 1 - lift * 0.009,
  };
}

function recordedPressureIsActive(frame: any): boolean {
  const pressure = frame?.play?.arcadeReplay?.metadata?.pressureEvent;
  if (!pressure) {
    return false;
  }
  if (typeof pressure === 'string') {
    return !['none', 'clean', 'clean-pocket'].includes(pressure.toLowerCase());
  }
  return typeof pressure === 'object';
}

function quarterbackRelease(
  tick: number,
  facing: number,
  frame: any,
): PlayerPresentationTransform | null {
  const throwFrame = Number(frame?.play?.arcadeReplay?.keyEvents?.throw?.simFrame);
  if (!Number.isFinite(throwFrame)) {
    return null;
  }
  const delta = tick - throwFrame;
  if (delta < -9 || delta > 10) {
    return null;
  }

  const pressureLean = recordedPressureIsActive(frame) ? 0.014 : 0;
  if (delta <= 0) {
    const load = smoothstep((delta + 9) / 9);
    return {
      kind: 'quarterback-release',
      offsetX: -facing * load * 0.48,
      offsetY: -Math.sin(load * Math.PI) * 0.42,
      rotation: -facing * (0.012 + load * 0.038 + pressureLean),
      scaleX: 1 + load * 0.018,
      scaleY: 1 - load * 0.012,
    };
  }

  const recoil = 1 - smoothstep(delta / 10);
  return {
    kind: 'quarterback-release',
    offsetX: facing * recoil * 0.42,
    offsetY: -recoil * 0.28,
    rotation: facing * (0.012 + recoil * 0.035) - facing * pressureLean,
    scaleX: 1 - recoil * 0.01,
    scaleY: 1 + recoil * 0.014,
  };
}

function trenchHandFight(
  tick: number,
  phase: number,
  facing: number,
  teamType: string,
): PlayerPresentationTransform {
  const hands = wave(tick, phase, 14);
  const leverage = wave(tick, phase + 4, 31);
  const strike = (hands + 1) * 0.5;
  const posture = teamType === 'offense' ? 1 : -1;

  return {
    kind: 'trench-hand-fight',
    offsetX: facing * (0.18 + strike * 0.46),
    offsetY: hands * 0.34,
    rotation: facing * posture * (0.018 + strike * 0.027 + leverage * 0.008),
    scaleX: 1.012 + strike * 0.018,
    scaleY: 0.992 - strike * 0.016,
  };
}

function coveragePosture(
  player: any,
  tick: number,
  phase: number,
  facing: number,
): PlayerPresentationTransform {
  const shuffle = wave(tick, phase, player?.moving ? 18 : 38);
  const activeFootwork = player?.moveStyle === 'backpedal'
    || player?.moveStyle === 'shuffle'
    || !!player?.moving;
  const intensity = activeFootwork ? 1 : 0.45;

  return {
    kind: 'coverage-posture',
    offsetX: -facing * shuffle * 0.2 * intensity,
    offsetY: -Math.abs(shuffle) * 0.42 * intensity,
    rotation: -facing * (0.014 + shuffle * 0.018) * intensity,
    scaleX: 1 + Math.abs(shuffle) * 0.008 * intensity,
    scaleY: 1 - Math.abs(shuffle) * 0.007 * intensity,
  };
}

function ballTrackingPosture(
  player: any,
  frame: any,
  facing: number,
): PlayerPresentationTransform | null {
  if (String(frame?.ball?.arcadeState || '') !== 'pass-flight') {
    return null;
  }
  const ballX = Number(frame?.ball?.ballX);
  const ballY = Number(frame?.ball?.ballY);
  const playerX = Number(player?.x);
  const playerY = Number(player?.y);
  if (![ballX, ballY, playerX, playerY].every(Number.isFinite)) {
    return null;
  }
  const deltaX = ballX - playerX;
  const deltaY = ballY - playerY;
  const distance = Math.hypot(deltaX, deltaY);
  const tracking = clamp01(1 - Math.max(0, distance - 36) / 260);
  if (tracking <= 0.08) {
    return null;
  }
  const ballSide = deltaY === 0 ? 0 : Math.sign(deltaY);

  return {
    kind: 'ball-tracking',
    offsetX: Math.sign(deltaX || facing) * tracking * 0.24,
    offsetY: -tracking * 0.28,
    rotation: ballSide * tracking * 0.034,
    scaleX: 1 - tracking * 0.008,
    scaleY: 1 + tracking * 0.018,
  };
}

function catchAnticipationPosture(
  player: any,
  frame: any,
  facing: number,
): PlayerPresentationTransform | null {
  if (String(frame?.ball?.arcadeState || '') !== 'pass-flight') {
    return null;
  }
  const role = String(player?.role || '');
  const targetRole = String(
    frame?.play?.arcadeReplay?.participants?.targetRole
      || frame?.play?.participants?.targetRole
      || frame?.play?.participants?.receiverRole
      || '',
  );
  if (!targetRole || targetRole !== role) {
    return null;
  }

  const ballX = Number(frame?.ball?.ballX);
  const ballY = Number(frame?.ball?.ballY);
  const playerX = Number(player?.x);
  const playerY = Number(player?.y);
  if (![ballX, ballY, playerX, playerY].every(Number.isFinite)) {
    return null;
  }

  const tuning = visualPolishTuning.catchAnticipation;
  const distance = Math.hypot(ballX - playerX, ballY - playerY);
  const tracking = clamp01(
    (tuning.maximumTrackingDistancePixels - distance)
      / (tuning.maximumTrackingDistancePixels - tuning.fullTrackingDistancePixels),
  );
  if (tracking < tuning.minimumTracking) {
    return null;
  }
  const ballSide = Math.sign(ballY - playerY);

  return {
    kind: 'catch-anticipation',
    offsetX: -facing * tracking * tuning.gatherPixels,
    offsetY: -tracking * tuning.liftPixels,
    rotation: ballSide * tracking * tuning.maximumRotationRadians,
    scaleX: 1 - tracking * 0.014,
    scaleY: 1 + tracking * 0.022,
  };
}

function routeBreakPlant(
  player: any,
  facing: number,
): PlayerPresentationTransform | null {
  const currentX = Number(player?.arcadeVisualVx);
  const currentY = Number(player?.arcadeVisualVy);
  const previousX = Number(player?.arcadePreviousVisualVx);
  const previousY = Number(player?.arcadePreviousVisualVy);
  if (![currentX, currentY, previousX, previousY].every(Number.isFinite)) {
    return null;
  }
  const currentSpeed = Math.hypot(currentX, currentY);
  const previousSpeed = Math.hypot(previousX, previousY);
  if (currentSpeed < 0.045 || previousSpeed < 0.045) {
    return null;
  }
  const normalizedDot = Math.max(
    -1,
    Math.min(1, (currentX * previousX + currentY * previousY) / (currentSpeed * previousSpeed)),
  );
  const angle = Math.acos(normalizedDot);
  const directionChange = clamp01((angle - 0.08) / 0.72);
  const speedGather = clamp01((previousSpeed - currentSpeed) / Math.max(0.08, previousSpeed * 0.58));
  const intensity = Math.max(directionChange, speedGather * 0.72);
  if (intensity < 0.16) {
    return null;
  }
  const cross = previousX * currentY - previousY * currentX;
  const plantSide = cross === 0 ? facing : Math.sign(cross);

  return {
    kind: 'route-break',
    offsetX: -facing * intensity * 0.42,
    offsetY: intensity * 0.28,
    rotation: plantSide * intensity * 0.062,
    scaleX: 1 + intensity * 0.028,
    scaleY: 1 - intensity * 0.034,
  };
}

function routeBodyLanguage(
  tick: number,
  phase: number,
  facing: number,
): PlayerPresentationTransform {
  const stride = wave(tick, phase, 12);
  const sell = wave(tick, phase + 7, 29);

  return {
    kind: 'route-body-language',
    offsetX: facing * sell * 0.18,
    offsetY: -Math.abs(stride) * 0.5,
    rotation: facing * (0.016 + sell * 0.018),
    scaleX: 1 + Math.abs(stride) * 0.009,
    scaleY: 1 - Math.abs(stride) * 0.007,
  };
}

function carrierLean(
  tick: number,
  phase: number,
  facing: number,
): PlayerPresentationTransform {
  const stride = wave(tick, phase, 10);
  return {
    kind: 'carrier-lean',
    offsetX: facing * 0.28,
    offsetY: -Math.abs(stride) * 0.55,
    rotation: facing * (0.026 + stride * 0.008),
    scaleX: 1.008 + Math.abs(stride) * 0.008,
    scaleY: 0.994 - Math.abs(stride) * 0.006,
  };
}

function contactEventForFrame(frame: any): any {
  const replay = frame?.play?.arcadeReplay;
  const fumbleEvent = replay?.keyEvents?.fumble
    || replay?.events?.find((event: any) => event?.type === 'fumble_forced')
    || null;
  const fumbleFrame = Number(fumbleEvent?.simFrame);
  const currentFrame = rawAnimationTick(frame);
  if (
    Number.isFinite(fumbleFrame)
    && currentFrame >= fumbleFrame
    && currentFrame - fumbleFrame <= visualPolishTuning.contactImpact.fumble.windowFrames
  ) {
    return fumbleEvent;
  }

  return replay?.keyEvents?.firstContact
    || replay?.keyEvents?.tackle
    || replay?.events?.find((event: any) => (
      event?.type === 'first_contact'
      || event?.type === 'tackle_started'
      || event?.type === 'ballcarrier_down'
    ))
    || null;
}

function contactImpact(
  player: any,
  frame: any,
  facing: number,
  phase: number,
): PlayerPresentationTransform | null {
  const tuning = visualPolishTuning.contactImpact;
  const contactEvent = contactEventForFrame(frame);
  const contactFrame = Number(contactEvent?.simFrame);
  if (!Number.isFinite(contactFrame)) {
    return null;
  }

  const fumbleImpact = contactEvent?.type === 'fumble_forced';
  const activeTuning = fumbleImpact ? tuning.fumble : tuning;
  const delta = rawAnimationTick(frame) - contactFrame;
  if (delta < 0 || delta > activeTuning.windowFrames) {
    return null;
  }

  const role = String(player?.role || '');
  const participants = frame?.play?.participants || {};
  const replayParticipants = frame?.play?.arcadeReplay?.participants || {};
  const eventPayload = contactEvent?.payload || {};
  const tacklerRoles = new Set([
    participants.tacklerRole,
    ...(Array.isArray(participants.tacklerRoles) ? participants.tacklerRoles : []),
    ...(Array.isArray(replayParticipants.tacklerRoles) ? replayParticipants.tacklerRoles : []),
    participants.forcedByRole,
    replayParticipants.forcedByRole,
    eventPayload.tacklerRole,
    eventPayload.defenderRole,
  ].filter(Boolean).map(String));
  const carrierRoles = new Set([
    eventPayload.carrierRole,
    frame?.ball?.arcadeOwnerRole,
    participants.runnerRole,
    participants.targetRole,
    participants.receiverRole,
    participants.returnerRole,
    participants.fumblerRole,
    replayParticipants.fumblerRole,
  ].filter(Boolean).map(String));
  const isCarrier = carrierRoles.has(role);
  const isTackler = tacklerRoles.has(role);
  if (!isCarrier && !isTackler) {
    return null;
  }
  if ((player?.down || player?.tackled) && delta > activeTuning.holdFrames) {
    return null;
  }

  const releaseProgress = delta <= activeTuning.holdFrames
    ? 0
    : (delta - activeTuning.holdFrames) / Math.max(1, activeTuning.windowFrames - activeTuning.holdFrames);
  const intensity = 1 - smoothstep(releaseProgress);
  const rotationSide = phase % 2 === 0 ? 1 : -1;
  const carrierStyle = String(
    participants.runnerRole === role
      ? participants.runnerPlayStyle
      : participants.targetRole === role || participants.receiverRole === role
        ? participants.targetPlayStyle
        : '',
  );
  const gainedYards = Number(frame?.play?.yards ?? frame?.play?.yardsGained ?? 0);
  const highRoll = gainedYards >= 6 && !fumbleImpact;

  if (isCarrier && carrierStyle === 'scat-back' && highRoll) {
    const contactProgress = clamp01(delta / Math.max(1, activeTuning.windowFrames));
    const spring = Math.sin(contactProgress * Math.PI);
    return {
      kind: 'contact-impact',
      offsetX: facing * spring * 0.7,
      offsetY: rotationSide * spring * 2.35,
      rotation: -rotationSide * facing * spring * 0.12,
      scaleX: 1 + spring * 0.045,
      scaleY: 1 - spring * 0.035,
    };
  }

  const carrierRecoilScale = carrierStyle === 'power-back' ? 0.48
    : carrierStyle === 'bell-cow' ? 0.68
      : carrierStyle === 'blocking-back' ? 0.42
        : 1;
  const lateralEvade = isCarrier && carrierStyle === 'receiving-back' && highRoll
    ? rotationSide * Math.sin(clamp01(delta / Math.max(1, activeTuning.windowFrames)) * Math.PI) * 1.25
    : 0;

  return {
    kind: 'contact-impact',
    offsetX: facing * intensity * (isCarrier ? -activeTuning.carrierRecoilPixels * carrierRecoilScale : activeTuning.tacklerDrivePixels),
    offsetY: intensity * (isCarrier ? 0.25 : -0.12) + lateralEvade,
    rotation: rotationSide * facing * intensity * activeTuning.maximumRotationRadians * (carrierStyle === 'blocking-back' ? 0.45 : 1),
    scaleX: 1 + intensity * activeTuning.horizontalCompression * (carrierStyle === 'power-back' ? 0.7 : 1),
    scaleY: 1 - intensity * activeTuning.verticalCompression * (carrierStyle === 'power-back' ? 0.7 : 1),
  };
}

export function resolvePlayerPresentationTransform(
  player: any,
  frame: any,
  teamType: string,
  rawFacing: number,
): PlayerPresentationTransform {
  const role = String(player?.role || '');
  const facing = rawFacing >= 0 ? 1 : -1;
  const tick = animationTick(frame);
  const phase = rolePhase(`${teamType}:${role}`) % 37;
  const ballState = String(frame?.ball?.arcadeState || '');
  const ballOwnerRole = String(frame?.ball?.arcadeOwnerRole || '');
  const celebration = celebrationTransform(player, frame, teamType, facing);
  if (celebration) {
    return celebration;
  }
  const impact = contactImpact(player, frame, facing, phase);
  if (impact) {
    return impact;
  }

  if (!isPresentationPlayer(player) || isGroundedOrFinishing(player)) {
    return { ...IDENTITY_TRANSFORM };
  }

  if (
    teamType === 'offense'
    && role === 'QB'
  ) {
    const release = quarterbackRelease(tick, facing, frame);
    if (release) {
      return release;
    }
  }

  if (teamType === 'offense' && ROUTE_ROLES.has(role) && player?.moving) {
    const anticipation = catchAnticipationPosture(player, frame, facing);
    if (anticipation) {
      return anticipation;
    }
    const routeBreak = routeBreakPlant(player, facing);
    if (routeBreak) {
      return routeBreak;
    }
  }

  if (teamType === 'defense' && COVERAGE_ROLES.has(role)) {
    const ballTracking = ballTrackingPosture(player, frame, facing);
    if (ballTracking) {
      return ballTracking;
    }
  }

  if (!visualPolishTuning.proceduralCadence.continuousMotionEnabled) {
    return { ...IDENTITY_TRANSFORM };
  }

  if (
    teamType === 'offense'
    && role === 'QB'
    && (ballState === 'snap' || ballState === 'carried')
  ) {
    return quarterbackFootwork(player, tick, phase, facing);
  }

  if (TRENCH_ROLES.has(role)) {
    const assignment = String(player?.arcadeAssignment || '');
    const activeEngagement = player?.moveStyle === 'engaged'
      || player?.engagementState === 'engaging'
      || player?.engagementState === 'screened'
      || /block|rush|protect|engage|pocket|contain/.test(assignment);
    if (activeEngagement) {
      return trenchHandFight(tick, phase, facing, teamType);
    }
  }

  if (ballOwnerRole === role && ballState === 'carried' && player?.moving) {
    return carrierLean(tick, phase, facing);
  }

  if (teamType === 'defense' && COVERAGE_ROLES.has(role)) {
    return coveragePosture(player, tick, phase, facing);
  }

  if (teamType === 'offense' && ROUTE_ROLES.has(role) && player?.moving) {
    return routeBodyLanguage(tick, phase, facing);
  }

  return { ...IDENTITY_TRANSFORM };
}
